import type { HotelSearchValues } from '../model/schema';
import { useHotelsWithHyperguest } from '@hyperguest/entities/hyperguest';
import { useMemo, useState } from 'react';
import { Button, Input, MultiSelect, Text } from '@/shared/ui';
import { hotelSearchSchema } from '../model/schema';
import css from './hyperguest-search.module.scss';

const todayIso = () => new Date().toISOString().slice(0, 10);

type Props = {
  initial: Partial<HotelSearchValues>;
  onSubmit: (values: HotelSearchValues) => void;
};

export const HotelSearchForm = ({ initial, onSubmit }: Props) => {
  const [checkIn, setCheckIn] = useState(initial.checkIn ?? todayIso());
  const [nights, setNights] = useState(initial.nights ?? 1);
  const [guests, setGuests] = useState(initial.guests ?? 2);
  const [hotelIds, setHotelIds] = useState<string[]>(initial.hotelIds ?? []);
  const [error, setError] = useState<string | null>(null);

  const hotelsQuery = useHotelsWithHyperguest();
  const options = useMemo(
    () =>
      (hotelsQuery.data ?? [])
        // Search hits HyperGuest with *their* numeric hotel id, not our
        // product UUID. Drop rows without one (shouldn't happen given the
        // `hasHyperGuestId` filter, but keeps the value non-null).
        .filter((row) => row.hyperGuestHotelId != null)
        .map((row) => ({
          value: String(row.hyperGuestHotelId),
          label:
            typeof row.title === 'string'
              ? row.title
              : (Array.isArray(row.title) &&
                  (
                    row.title as Array<{ languageCode: string; text: string }>
                  ).find((t) => t.languageCode === 'en')?.text) ||
                row.id,
        })),
    [hotelsQuery.data]
  );

  const onSubmitInternal = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = hotelSearchSchema.safeParse({
      checkIn,
      nights,
      guests,
      hotelIds,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Invalid form');
      return;
    }
    if (parsed.data.checkIn < todayIso()) {
      setError('Check-in cannot be in the past');
      return;
    }
    setError(null);
    onSubmit(parsed.data);
  };

  return (
    <form className={css.form} onSubmit={onSubmitInternal}>
      <div className={css.formRow}>
        <div className={css.formField}>
          <Text as="label" size="sm" weight="medium">
            Check-in
          </Text>
          <Input
            type="date"
            min={todayIso()}
            value={checkIn}
            onChange={(e) => setCheckIn(e.target.value)}
          />
        </div>
        <div className={css.formField}>
          <Text as="label" size="sm" weight="medium">
            Nights
          </Text>
          <Input
            type="number"
            min={1}
            value={nights}
            onChange={(e) => setNights(Number(e.target.value))}
          />
        </div>
        <div className={css.formField}>
          <Text as="label" size="sm" weight="medium">
            Guests
          </Text>
          <Input
            type="number"
            min={1}
            value={guests}
            onChange={(e) => setGuests(Number(e.target.value))}
          />
        </div>
      </div>
      <div className={css.formField}>
        <Text as="label" size="sm" weight="medium">
          Hotels
        </Text>
        <MultiSelect
          options={options}
          values={hotelIds}
          onChange={(vals) => setHotelIds(vals)}
          searchable
          placeholder={hotelsQuery.isLoading ? 'Loading…' : 'Pick hotels'}
        />
      </div>
      {error && (
        <Text size="sm" variant="error">
          {error}
        </Text>
      )}
      <div className={css.formActions}>
        <Button
          type="submit"
          variant="primary"
          disabled={hotelIds.length === 0}
        >
          Search
        </Button>
      </div>
    </form>
  );
};
